import { EmptyState, InfoCard } from '@backstage/core-components';
import type { Entity } from '@backstage/catalog-model';
import { useEntity } from '@backstage/plugin-catalog-react';
import {
  Box,
  Chip,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  Typography,
  makeStyles,
} from '@material-ui/core';

type UpgradePathStep = {
  order?: number | string;
  action?: string;
  details?: string;
  is_critical?: boolean;
  isCritical?: boolean;
};

type EksUpgradeReport = {
  generatedAt?: string;
  readiness?: string;
  currentVersion?: string;
  targetVersion?: string;
  issueCounts?: {
    critical?: number;
    warning?: number;
    info?: number;
  };
  upgradePath?: UpgradePathStep[];
};

const useStyles = makeStyles(theme => ({
  summary: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  order: {
    alignItems: 'center',
    background: theme.palette.grey[200],
    borderRadius: '50%',
    display: 'inline-flex',
    flex: '0 0 auto',
    fontWeight: 700,
    height: 28,
    justifyContent: 'center',
    marginRight: theme.spacing(2),
    width: 28,
  },
  item: {
    alignItems: 'flex-start',
    paddingLeft: 0,
    paddingRight: 0,
  },
  actionRow: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  details: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
    wordBreak: 'break-word',
  },
}));

function valueText(value: unknown, fallback = 'unknown'): string {
  if (value === undefined || value === null || value === '') {
    return fallback;
  }
  return String(value);
}

function sortedUpgradePath(steps: UpgradePathStep[]): UpgradePathStep[] {
  return [...steps].sort((a, b) => {
    const aOrder = Number(a.order);
    const bOrder = Number(b.order);
    const aHasOrder = Number.isFinite(aOrder);
    const bHasOrder = Number.isFinite(bOrder);

    if (aHasOrder && bHasOrder) {
      return aOrder - bOrder;
    }
    if (aHasOrder) {
      return -1;
    }
    if (bHasOrder) {
      return 1;
    }
    return 0;
  });
}

function count(value: unknown): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function stepCountText(stepCount: number): string {
  return `${stepCount} ${stepCount === 1 ? 'step' : 'steps'}`;
}

function getUpgradeReport(entity: Entity) {
  return (entity.spec as { upgradeReport?: EksUpgradeReport } | undefined)
    ?.upgradeReport;
}

export function EksUpgradePathContent() {
  const { entity } = useEntity();
  const classes = useStyles();
  const report = getUpgradeReport(entity);
  const steps = sortedUpgradePath(report?.upgradePath ?? []);
  const issues = report?.issueCounts ?? {};

  if (!report || steps.length === 0) {
    return (
      <EmptyState
        missing="data"
        title="No EKS upgrade path"
        description="This cluster entity does not include spec.upgradeReport.upgradePath."
      />
    );
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <InfoCard title="EKS upgrade path">
          <Box className={classes.summary}>
            <Chip
              size="small"
              label={`Readiness: ${valueText(report.readiness)}`}
            />
            <Chip
              size="small"
              label={`${valueText(report.currentVersion)} -> ${valueText(
                report.targetVersion,
              )}`}
            />
            <Chip size="small" label={stepCountText(steps.length)} />
            <Chip
              size="small"
              label={`${count(issues.critical)} critical / ${count(
                issues.warning,
              )} warning / ${count(issues.info)} info`}
            />
            {report.generatedAt && (
              <Chip size="small" label={`Generated: ${report.generatedAt}`} />
            )}
          </Box>

          <List disablePadding>
            {steps.map((step, index) => {
              const order = valueText(step.order, String(index + 1));
              const critical = Boolean(step.is_critical || step.isCritical);

              return (
                <Box key={`${order}-${index}`}>
                  {index > 0 && <Divider />}
                  <ListItem className={classes.item}>
                    <Box component="span" className={classes.order}>
                      {order}
                    </Box>
                    <ListItemText
                      primary={
                        <Box className={classes.actionRow}>
                          <Typography variant="subtitle2">
                            {valueText(step.action, 'Upgrade step')}
                          </Typography>
                          {critical && (
                            <Chip
                              size="small"
                              color="secondary"
                              label="Critical"
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        step.details ? (
                          <Typography
                            variant="body2"
                            className={classes.details}
                          >
                            {step.details}
                          </Typography>
                        ) : undefined
                      }
                    />
                  </ListItem>
                </Box>
              );
            })}
          </List>
        </InfoCard>
      </Grid>
    </Grid>
  );
}
